import React, {
    ReactElement, useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiPencil, BiTrash } from "react-icons/bi";
import Confirm from "../../../../components/confirm/Confirm";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import FieldType from "../../../../components/pickers/field-type/FieldType";
import { FormFieldType } from "../../../../__generated__/api";
import Select from "../../../../components/select/Select";
import { NamedParameters } from "./Template";
import SortableList from "../../../../components/sortable-list/SortableList";

type Json = Record<string, unknown>;

export default function InputList({
    json, onChange, rootJson,
}: {
    json : Json[];
    onChange: (value: Json[]) => void;
    rootJson: Json;

}): ReactElement {
    const [editTemplate, setEditTemplate] = useState<Json | undefined>();

    const [editOption, setEditOption] = useState<{index: number; option: Json} | undefined>();

    const apis = (rootJson.apis ?? []) as Json[];
    const selectionOptionsApi = editTemplate ? apis.find(api => api.id === (editTemplate.optionsApi as Json)?.apiId) : undefined;

    return (
        <>
            {editTemplate != null && (
                <Confirm
                    shouldShow
                    confirmText="Save"
                    size="lg"
                    onHide={() => setEditTemplate(undefined)}
                    onConfirm={evt => {
                        evt?.stopPropagation();
                        const parameters = (json ?? []).filter(row => row.id !== editTemplate.id);

                        if (editTemplate) {
                            onChange([
                                ...parameters,
                                (editTemplate as Json),
                            ]);
                        }

                        setEditTemplate(undefined);
                    }}
                >
                    <div className="d-flex">
                        <div className="col">
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Id</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Id"
                                    required
                                    onChange={evt => setEditTemplate({ ...editTemplate, id: evt.target.value })}
                                    value={editTemplate.id as string ?? ""}
                                />
                                <Form.Text>
                                    The id of the parameter
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setEditTemplate({ ...editTemplate, name: evt.target.value })}
                                    value={editTemplate.name as string ?? ""}
                                />
                                <Form.Text>
                                    The name of the parameter
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Description"
                                    required
                                    as="textarea"
                                    onChange={evt => setEditTemplate({ ...editTemplate, description: evt.target.value })}
                                    value={editTemplate.description as string ?? ""}
                                />
                                <Form.Text>
                                    The description of the parameter
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Is Required</Form.Label>
                                <BooleanSelect
                                    onChange={value => setEditTemplate({ ...editTemplate, isRequired: value })}
                                    value={editTemplate.isRequired as boolean ?? ""}
                                />
                                <Form.Text>
                                    Is the user required
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Placeholder</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Placeholder"
                                    required
                                    onChange={evt => setEditTemplate({ ...editTemplate, placeholder: evt.target.value })}
                                    value={editTemplate.placeholder as string ?? ""}
                                />
                                <Form.Text>
                                    The id of the parameter
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Type</Form.Label>
                                <FieldType
                                    className="text-gray"
                                    onChange={value => setEditTemplate({ ...editTemplate, type: value as string })}
                                    value={editTemplate.type as FormFieldType ?? FormFieldType.Text}
                                />
                                <Form.Text>
                                    The type of the parameter
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">
                                    Default Value
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Default"
                                    onChange={evt => setEditTemplate({
                                        ...editTemplate,
                                        defaultValue: {
                                            string: evt.target.value,
                                        },
                                    })}
                                    value={(editTemplate.defaultValue as Json)?.string as string ?? ""}
                                />
                                <Form.Text>
                                    The default value. (Can only be string based)
                                </Form.Text>
                            </Form.Group>
                        </div>

                        {(editTemplate.type === FormFieldType.Dropdown
                            || editTemplate.type === FormFieldType.AsyncDropdown
                            || editTemplate.type === FormFieldType.MultiSelectDropdown
                            || editTemplate.type === FormFieldType.MultiLevelDropdown
                        ) && (
                            <div className="col ms-2 ps-2 border-start">
                                {editTemplate.optionsApi != null && (
                                    <>
                                        <Select
                                            className="mb-2"
                                            onChange={value => setEditTemplate({
                                                ...editTemplate,
                                                optionsApi: {
                                                    ...(editTemplate.optionsApi as Json),
                                                    apiId: value.value,
                                                },
                                            })}
                                            value={!selectionOptionsApi ? undefined : {
                                                label: selectionOptionsApi.name as string,
                                                value: selectionOptionsApi.id as string,
                                            }}
                                            options={apis.map(api => ({
                                                label: api.name as string,
                                                value: api.id as string,
                                            }))}
                                        />

                                        <NamedParameters
                                            json={(editTemplate.optionsApi as Json)?.parameters as Json[] ?? []}
                                            onChange={parameters => setEditTemplate({
                                                ...editTemplate,
                                                optionsApi: {
                                                    ...(editTemplate.optionsApi as Json),
                                                    parameters,
                                                },
                                            })}

                                        />
                                    </>
                                )}

                                {editTemplate.optionsApi == null && (
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm p-1"
                                            onClick={() => setEditTemplate({
                                                ...editTemplate,
                                                optionsApi: {},
                                            })}
                                        >
                                            Dynamic Choices
                                        </button>
                                    </div>
                                )}

                                {editTemplate.options != null && (
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm p-1"
                                            onClick={() => setEditOption({
                                                index: (editTemplate.options as Json[] ?? []).length,
                                                option: {},
                                            })}
                                        >
                                            Add Option
                                        </button>
                                    </div>
                                )}

                                {editTemplate.options != null && (
                                    <SortableList
                                        items={editTemplate.options as Json[] ?? []}
                                        getId={row => row.value as string}
                                        itemClassName="list-group-item d-flex justify-content-between"
                                        listClassName="list-group"
                                        onReorder={values => {
                                            setEditTemplate({ ...editTemplate, options: values });
                                        }}
                                        render={(option, index) => (
                                            <>
                                                <div>
                                                    <h5>
                                                        {option.label as string}
                                                    </h5>
                                                    <small>
                                                        {option.value as string}
                                                    </small>
                                                </div>
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-none"
                                                        type="button"
                                                        onClick={() => {
                                                            setEditOption({
                                                                index,
                                                                option,
                                                            });
                                                        }}
                                                    >
                                                        <BiPencil className="text-light" />
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-none"
                                                        type="button"
                                                        onClick={() => {
                                                            const copy = [...(editTemplate.options as Json[] ?? [])];
                                                            copy.splice(index);

                                                            setEditTemplate({
                                                                ...editTemplate,
                                                                options: copy,
                                                            });
                                                        }}
                                                    >
                                                        <BiTrash className="text-light" />
                                                    </button>

                                                </div>

                                            </>
                                        )}
                                    />

                                )}
                                {editTemplate.options == null && (
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm p-1"
                                            onClick={() => setEditTemplate({
                                                ...editTemplate,
                                                options: [],
                                            })}
                                        >
                                            Static Choices
                                        </button>
                                    </div>
                                )}

                                {editTemplate.type === FormFieldType.MultiLevelDropdown && (
                                    <Form.Group className="mt-3">
                                        <Form.Label className="fw-bolder">
                                            Levels
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            as="textarea"
                                            placeholder="Default"
                                            onChange={evt => setEditTemplate({
                                                ...editTemplate,
                                                levels: evt.target.value?.split("\n"),
                                            })}
                                            value={(editTemplate.levels as string[])?.join("\n")}
                                        />
                                        <Form.Text>
                                            what are the levels of each choice?
                                        </Form.Text>
                                    </Form.Group>
                                )}
                                <Form.Group className="mt-3">
                                    <Form.Label className="fw-bolder">
                                        Empty options message
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        as="textarea"
                                        placeholder="Please see "
                                        onChange={evt => setEditTemplate({
                                            ...editTemplate,
                                            emptyOptionsMessage: evt.target.value,
                                        })}
                                        value={editTemplate.emptyOptionsMessage as string}
                                    />
                                    <Form.Text>
                                        what are the levels of each choice?
                                    </Form.Text>
                                </Form.Group>

                            </div>
                        )}

                    </div>

                </Confirm>
            )}

            <button
                type="button"
                className="btn btn-primary btn-sm py-1 px-2 mb-2"
                onClick={() => setEditTemplate({})}
            >
                Add
            </button>

            {editOption != null && (
                <Confirm
                    shouldShow
                    confirmText="Save"
                    size="lg"
                    onHide={() => setEditOption(undefined)}
                    onConfirm={evt => {
                        evt?.stopPropagation();
                        const copy = [...(editTemplate?.options as Json[] ?? [])];
                        copy[editOption.index] = editOption.option;
                        setEditTemplate({
                            ...editTemplate,
                            options: copy,
                        });

                        setEditOption(undefined);
                    }}
                >
                    <div className="d-flex">
                        <div className="col">
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Value</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Id"
                                    required
                                    onChange={evt => setEditOption({
                                        ...editOption,
                                        option: {
                                            ...editOption.option,
                                            value: evt.target.value,
                                        },
                                    })}
                                    value={editOption?.option.value as string ?? ""}
                                />
                                <Form.Text>
                                    The value
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bolder">Label</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Label"
                                    required
                                    onChange={evt => setEditOption({
                                        ...editOption,
                                        option: {
                                            ...editOption.option,
                                            label: evt.target.value,
                                        },
                                    })}
                                    value={editOption.option.label as string ?? ""}
                                />
                                <Form.Text>
                                    The display label
                                </Form.Text>
                            </Form.Group>

                        </div>

                    </div>

                </Confirm>
            )}

            <SortableList
                items={json}
                getId={row => row.id as string}
                itemClassName="list-group-item d-flex justify-content-between"
                listClassName="list-group"
                onReorder={onChange}
                render={parameter => (
                    <>
                        <div>
                            <h5>
                                {parameter.name as string}
                                <small className="text-light ms-2">
                                    {parameter.type as string}
                                    {" | "}
                                    {parameter.placeholder as string}
                                </small>
                            </h5>
                            <small>
                                {parameter.description as string}
                            </small>
                        </div>
                        <div>
                            <button
                                className="btn btn-sm btn-none"
                                type="button"
                                onClick={() => {
                                    setEditTemplate(parameter);
                                }}
                            >
                                <BiPencil className="text-light" />
                            </button>

                            <button
                                className="btn btn-sm btn-none"
                                type="button"
                                onClick={() => {
                                    onChange(json.filter(row => row.id !== parameter.id));
                                }}
                            >
                                <BiTrash className="text-light" />
                            </button>

                        </div>

                    </>
                )}
            />
        </>
    );
}
